import { createPortal } from "react-dom";
import { ManagePlanUsageBar } from "./ManagePlanUsageBar";
import type { AiCreditPackOption } from "./types";
import type { RefObject } from "react";
import type { BillingInterval } from "store/slices/reduxApi/billing";

type ManagePlanCreditPanelProps = {
  billingInterval: BillingInterval;
  availableCreditPackOptions: AiCreditPackOption[];
  currentPlanCredits: number;
  currentUsageCredits: number;
  selectedCredits: number | null;
  selectedPack: AiCreditPackOption | null;
  isDropdownOpen: boolean;
  dropdownPosition: { top: number; left: number; width: number } | null;
  triggerRef: RefObject<HTMLButtonElement>;
  dropdownRef: RefObject<HTMLDivElement>;
  onToggleDropdown: () => void;
  onSelectCredits: (credits: number | null) => void;
};

function formatUsd(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatCreditPackLabel(
  credits: number,
  priceUsd: number,
  billingInterval: BillingInterval,
) {
  const intervalLabel = billingInterval === "annual" ? "annually" : "monthly";
  return `${credits.toLocaleString()} credits/month (${formatUsd(priceUsd)} ${intervalLabel})`;
}

function SparklesIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 text-gray-500"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3v6" />
      <path d="M12 15v6" />
      <path d="M5.64 5.64l4.24 4.24" />
      <path d="M14.12 14.12l4.24 4.24" />
      <path d="M3 12h6" />
      <path d="M15 12h6" />
      <path d="M5.64 18.36l4.24-4.24" />
      <path d="M14.12 9.88l4.24-4.24" />
    </svg>
  );
}

export function ManagePlanCreditPanel({
  billingInterval,
  availableCreditPackOptions,
  currentPlanCredits,
  currentUsageCredits,
  selectedCredits,
  selectedPack,
  isDropdownOpen,
  dropdownPosition,
  triggerRef,
  dropdownRef,
  onToggleDropdown,
  onSelectCredits,
}: ManagePlanCreditPanelProps) {
  const selectedPrice = selectedPack?.priceUsd ?? 0;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50/60 px-4 py-3">
        <SparklesIcon />
        <div className="text-sm font-semibold text-gray-950">
          Additional AI Credits
        </div>
      </div>
      <div className="px-4 py-4">
        <div className="space-y-4">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-400">
              Current plan
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-950">
              {currentPlanCredits.toLocaleString()} Org Credits
            </div>
          </div>

          <ManagePlanUsageBar
            used={currentUsageCredits}
            total={selectedPack ? selectedPack.credits : currentPlanCredits}
            colorClassName="bg-violet-500"
          />

          <div className="my-3 h-px bg-gray-200" />

          <label
            htmlFor="usage-credit-pack"
            className="block text-sm text-gray-500"
          >
            {currentPlanCredits > 0
              ? "Adjust Org Credit Pack (Recurring Monthly)"
              : "Add org-wide credits"}
          </label>
          <div className="mt-3">
            <button
              id="usage-credit-pack"
              ref={triggerRef}
              type="button"
              onClick={onToggleDropdown}
              className="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-gray-200 bg-white px-4 text-left text-sm text-gray-950 shadow-[0_1px_2px_rgba(0,0,0,0.05)] outline-none transition hover:bg-gray-50 focus:border-gray-300 focus:ring-[3px] focus:ring-gray-200/70"
            >
              <span>
                {selectedPack
                  ? formatCreditPackLabel(
                      selectedPack.credits,
                      selectedPrice,
                      billingInterval,
                    )
                  : selectedCredits === 0
                    ? "No additional credits"
                    : "Select AI Credit Pack"}
              </span>
              <svg
                aria-hidden="true"
                className={`h-4 w-4 text-gray-500 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            {isDropdownOpen && dropdownPosition
              ? createPortal(
                  <div
                    ref={dropdownRef}
                    className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)]"
                    style={{
                      position: "fixed",
                      top: dropdownPosition.top,
                      left:
                        dropdownPosition.left + dropdownPosition.width - 355,
                      width: 355,
                      zIndex: 2000,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectCredits(0)}
                      className={`flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left text-sm transition ${selectedCredits === 0 ? "bg-gray-50 text-gray-950" : "bg-white text-gray-900 hover:bg-gray-50"}`}
                    >
                      <span>No additional credits</span>
                      <span
                        className={`text-base leading-none ${selectedCredits === 0 ? "text-gray-950" : "text-transparent"}`}
                      >
                        ✓
                      </span>
                    </button>
                    {availableCreditPackOptions.map((option) => {
                      const isSelected = option.credits === selectedCredits;
                      const isCurrent = option.credits === currentPlanCredits;

                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => onSelectCredits(option.credits)}
                          className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm transition ${isSelected ? "cursor-pointer bg-gray-50 text-gray-950" : "cursor-pointer bg-white text-gray-900 hover:bg-gray-50"}`}
                        >
                          <span>
                            {formatCreditPackLabel(
                              option.credits,
                              option.priceUsd,
                              billingInterval,
                            )}
                            {isCurrent ? (
                              <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                                Current
                              </span>
                            ) : null}
                          </span>
                          <span
                            className={`text-base leading-none ${isSelected ? "text-gray-950" : "text-transparent"}`}
                          >
                            ✓
                          </span>
                        </button>
                      );
                    })}
                  </div>,
                  document.body,
                )
              : null}
          </div>
        </div>
      </div>
    </div>
  );
}
